import { intlFormat } from 'date-fns';
import { useState } from 'react';
import { trackError, ValidationError } from '#api/errors.ts';
import { InlineButton } from '#components/atoms/buttons/inline-button/InlineButton.tsx';
import { DetailCard } from '#components/atoms/cards/detail-card/DetailCard.tsx';
import { DetailList } from '#components/patterns/detail-list/DetailList.tsx';
import { DetailListRow } from '#components/patterns/detail-list/DetailListRow.tsx';
import type { DailyUsage, PaidPlan } from '#dn-types/billing.ts';
import { useAuth } from '#global/context/auth/useAuth.tsx';
import { DowngradePlanModal } from '#screens/settings/billing/downgrade-plan-modal/DowngradePlanModal.tsx';
import { useCancelPlanMutation } from '#screens/settings/billing/summary-card/SummaryCard.queries.ts';
import { HostUsageChart } from '#screens/settings/billing/usage-chart/HostUsageChart.tsx';
import styles from './SummaryCard.module.css';

type Props = {
  billingDetails: PaidPlan;
};

function calculateMonthToDate(usage: DailyUsage[]) {
  return usage.reduceRight<{ asOfDate: string | null; monthToDateUsage: number }>(
    (accum, curr) => {
      if (!accum.asOfDate && curr.cost !== null) {
        accum.asOfDate = curr.date;
      }
      accum.monthToDateUsage += curr.cost ?? 0;
      return accum;
    },
    { asOfDate: null, monthToDateUsage: 0 }
  );
}

export function SummaryCard({ billingDetails }: Props) {
  const { asOfDate, monthToDateUsage } = calculateMonthToDate(billingDetails.usage);
  const userType = useAuth().user?.type;

  const [isCancellingPlan, setIsCancellingPlan] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const cancelPlanMutation = useCancelPlanMutation();

  const onPlanDowngrade = async () => {
    setErrorMessage(null);

    try {
      await cancelPlanMutation.mutateAsync();
      setIsCancellingPlan(false);
    } catch (err) {
      if (err instanceof ValidationError) {
        setErrorMessage(err.message);
      } else {
        trackError(err);
        setErrorMessage(
          'We had an issue cancelling your plan. If you have further problems, contact billing@defined.net'
        );
      }
    }
  };

  if (!userType) {
    return null;
  }

  return (
    <div className={styles.SummaryCard}>
      <DetailCard className={styles.SummaryCard_card}>
        <div>
          <h2 className="h4">Summary</h2>
          <p className="h2 mb-4">
            {new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(monthToDateUsage / 100)}
          </p>
          <p className="p-md">
            Month-to-date charges
            {asOfDate ?
              ` as of ${intlFormat(new Date(asOfDate), {
                month: 'numeric',
                day: 'numeric',
                year: 'numeric',
                timeZone: 'utc',
              })} UTC`
            : null}
          </p>
          <DetailList>
            <DetailListRow
              label="Current hosts"
              value={new Intl.NumberFormat().format(billingDetails.currentHostCount)}
            />
            <DetailListRow
              label="Cost per host"
              value={`${new Intl.NumberFormat(undefined, {
                style: 'currency',
                currency: 'USD',
                trailingZeroDisplay: 'stripIfInteger',
              }).format(billingDetails.pricePerHost / 100)}/mo`}
            />
            <DetailListRow
              label="Plan"
              value={
                <div className="flex gap-8">
                  Pro{' '}
                  {userType === 'user' ?
                    <InlineButton className="a" onClick={() => setIsCancellingPlan(true)}>
                      Downgrade
                    </InlineButton>
                  : null}
                </div>
              }
            />
            <DetailListRow
              label="Next billing date"
              value={`${intlFormat(new Date(billingDetails.nextBill.date), {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                timeZone: 'utc',
              })} UTC`}
            />
            {billingDetails.prevBill ?
              <DetailListRow
                label="Last bill"
                value={`${new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(
                  billingDetails.prevBill.amount / 100
                )} on ${intlFormat(new Date(billingDetails.prevBill.date), {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                  timeZone: 'utc',
                })} UTC`}
              />
            : null}
          </DetailList>
        </div>
      </DetailCard>
      {billingDetails.usage.length ?
        <DetailCard className={styles.SummaryCard_card}>
          <HostUsageChart usage={billingDetails.usage} />
        </DetailCard>
      : null}
      {isCancellingPlan ?
        <DowngradePlanModal
          onAccept={onPlanDowngrade}
          onCancel={() => {
            setIsCancellingPlan(false);
            setErrorMessage(null);
          }}
          errorMessage={errorMessage}
        />
      : null}
    </div>
  );
}
